import { v4 as uuid4 } from "uuid"
import Logger from "../../../core/internal/logger"
import ErrorUtil from "../../../core/internal/util/ErrorUtil"
import { HackleApp } from "../HackleWebAppClient"
import { BridgeResponseDispatcher } from "./BridgeResponseDispatcher"
import { Invocation, invocationRequestDto } from "./Invocation"
import InvocationResponseResolver from "./InvocationResponseResolver"

export abstract class HackleAppInvocator {
  abstract process<P extends Record<string, any> | never, R>(invocation: Invocation<P, R>): R | undefined

  protected payload<P extends Record<string, any> | never, R>(
    invocation: Invocation<P, R>,
    messageId?: string
  ): string {
    return JSON.stringify(invocationRequestDto(invocation, messageId))
  }
}

export class PromptAppInvocator extends HackleAppInvocator {
  constructor(private readonly responseResolver: InvocationResponseResolver) {
    super()
  }

  process<P extends Record<string, any> | never, R>(invocation: Invocation<P, R>): R | undefined {
    return this.responseResolver.resolve<R>(window.prompt(this.payload(invocation)))?.data
  }
}

export class FunctionAppInvocator extends HackleAppInvocator {
  constructor(private readonly responseResolver: InvocationResponseResolver) {
    super()
  }

  process<P extends Record<string, any> | never, R>(invocation: Invocation<P, R>): R | undefined {
    if (!window._hackleApp?.invoke) {
      throw new Error("window._hackleApp.invoke is not implemented")
    }

    return this.responseResolver.resolve<R>(window._hackleApp.invoke(this.payload(invocation)))?.data
  }
}

export class MessageAppInvocator extends HackleAppInvocator {
  private constructor(
    private readonly postMessage: (payload: string) => void,
    private readonly responseDispatcher: BridgeResponseDispatcher,
    private readonly generateMessageId: () => string
  ) {
    super()
  }

  static createOrNull(
    hackleApp: HackleApp,
    responseDispatcher: BridgeResponseDispatcher,
    generateMessageId: () => string = () => uuid4()
  ): MessageAppInvocator | null {
    if (typeof hackleApp.postMessage !== "function") {
      return null
    }

    return new MessageAppInvocator(
      (payload: string) => hackleApp.postMessage!(payload),
      responseDispatcher,
      generateMessageId
    )
  }

  process<P extends Record<string, any> | never, R>(invocation: Invocation<P, R>): R | undefined {
    const messageId = this.generateMessageId()

    const completion = this.responseDispatcher.register(messageId).catch((e) => {
      Logger.log.warn(
        `Bridge request failed. [command=${invocation.command}, messageId=${messageId}, error=${ErrorUtil.asError(e)}]`
      )
    })

    try {
      this.postMessage(this.payload(invocation, messageId))
    } catch {
      this.responseDispatcher.cancel(messageId)
    }

    return completion as unknown as R
  }
}
